import { createEntityByIdSource, readWhenDownloaded, type EntityByIdStorage } from 'serviceWorker/channelServers/entitySource';
import type { EntityByIdSource } from 'serviceWorker/channelServers/serveEntityRequests';
import type { InspectionTemplateEntity } from 'serviceWorker/repository/inspectionTemplate/entity';
import * as dbInspectionTemplates from 'serviceWorker/db/inspectionTemplates/inspectionTemplates';
import * as dbInspectionTemplatesService from 'serviceWorker/db/inspectionTemplates/inspectionTemplatesService';
import { inspectionTemplateContract } from 'shared/domain/inspectionTemplate/inspectionTemplateContract';
import type { InspectionTemplateModel } from 'shared/domain/template/inspectionTemplateModel';
import { canSyncTemplates, inspectionTemplatesSync } from './index';
import { readInspectionTemplateModels, readStoredInspectionTemplate } from './inspectionTemplateReads';

export const inspectionTemplateStorage: EntityByIdStorage<InspectionTemplateModel> = {
  service: dbInspectionTemplatesService,
  contract: inspectionTemplateContract,
  ...inspectionTemplatesSync,
  readAll: readInspectionTemplateModels,
  readOne: readStoredInspectionTemplate,
};

const storedTemplates = createEntityByIdSource(inspectionTemplateStorage);

/**
 * Templates of the selected project. Standard users have no access to
 * templates and nothing is ever downloaded for them: their reads are
 * answered at once instead of waiting for a download that never comes.
 */
export const inspectionTemplateSource: EntityByIdSource<InspectionTemplateModel> & {
  getAllEntities: () => Promise<InspectionTemplateEntity[]>;
} = {
  getAll: async () => ((await canSyncTemplates()) ? storedTemplates.getAll() : []),

  getOneById: async (id) => {
    if (!(await canSyncTemplates())) {
      throw new Error('Inspection templates are not available for this role');
    }
    return storedTemplates.getOneById(id);
  },

  sync: () => storedTemplates.sync(),

  /** Stored entities for the worker's own calculations (analytics). */
  getAllEntities: async () =>
    (await canSyncTemplates())
      ? readWhenDownloaded({ ...inspectionTemplateStorage, syncAfterRead: false }, () => dbInspectionTemplates.get())
      : [],
};
